import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wallet, ShieldCheck, X, Save, RotateCcw, Scale, Layers
} from 'lucide-react';

// 🎯 إعدادات ميزة إدارة المرافق/مقدمي الخدمة (FM/SP) على مستوى المؤسسة -
// نفس شكل AISettingsModal بالظبط. لما مفعّلة، شجرة الحسابات بتاخد قسم
// الذمم المدينة تلقائيًا (شوف /gl/seed-fm-sp-chart-of-accounts/{org_id})،
// وطلبات البورتال المكتملة بترحّل قيود استحقاق ودفع في دفتر الأستاذ
const FinanceSettingsModal = ({ isOpen, onClose, onSave, currentSettings, orgId }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [settings, setSettings] = useState({
    org_id: orgId,
    enable_fm_sp: false,
    sp_accounting_method: 'NET',
  });

  useEffect(() => {
    if (isOpen) {
      setSettings({
        org_id: orgId,
        enable_fm_sp: !!currentSettings?.enable_fm_sp,
        sp_accounting_method: currentSettings?.sp_accounting_method || 'NET',
      });
    }
  }, [currentSettings, isOpen, orgId]);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] flex items-center justify-center p-4 transition-all ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-full max-w-xl rounded-3xl shadow-2xl border border-line overflow-hidden animate-in zoom-in-95 duration-300">

        <div className="bg-primary-dark p-6 text-ink-inverse flex justify-between items-center relative">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/30 rounded-2xl backdrop-blur-sm border border-white/10">
              <Wallet size={28} className="text-ink-inverse/70" />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tight">{t('finance_settings.title')}</h2>
              <p className="text-ink-inverse/70 text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                {t('finance_settings.sub_title')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-ink-inverse/70 hover:text-ink-inverse">
            <X size={24}/>
          </button>
        </div>

        <div className="p-8 space-y-8 max-h-[75vh] overflow-y-auto custom-scrollbar">

          <div className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all ${settings.enable_fm_sp ? 'bg-primary-soft border-primary shadow-inner' : 'bg-surface-panel border-line-subtle'}`}>
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${settings.enable_fm_sp ? 'bg-primary text-ink-inverse' : 'bg-status-neutral-bg text-status-neutral'}`}>
                <Layers size={20} />
              </div>
              <div>
                <p className="font-black text-ink text-sm">{t('finance_settings.enable_label')}</p>
                <p className="text-[10px] text-ink-secondary font-bold uppercase italic tracking-wider">
                  {t('finance_settings.enable_sub')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer scale-110">
              <input
                type="checkbox"
                checked={Boolean(settings.enable_fm_sp)}
                onChange={(e) => setSettings({ ...settings, enable_fm_sp: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-12 h-6 bg-line peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {settings.enable_fm_sp && (
            <div className="space-y-4 animate-in slide-in-from-top-2">
              <div className="flex items-center gap-2 text-primary mb-2">
                <Scale size={18} />
                <h3 className="font-black uppercase tracking-widest text-sm">{t('finance_settings.method_title')}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => setSettings({ ...settings, sp_accounting_method: 'NET' })}
                  className={`cursor-pointer border-2 p-4 rounded-xl flex flex-col gap-2 transition-all ${settings.sp_accounting_method === 'NET' ? 'border-primary bg-primary-soft/50 shadow-md' : 'border-line hover:border-primary/50 bg-surface-card'}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="text-primary font-black uppercase text-sm">{t('finance_settings.method_net')}</div>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${settings.sp_accounting_method === 'NET' ? 'border-primary' : 'border-line'}`}>
                      {settings.sp_accounting_method === 'NET' && <div className="w-2 h-2 bg-primary rounded-full"/>}
                    </div>
                  </div>
                  <p className="text-[10px] text-ink-secondary font-bold leading-relaxed">{t('finance_settings.method_net_desc')}</p>
                </div>

                <div
                  onClick={() => setSettings({ ...settings, sp_accounting_method: 'GROSS' })}
                  className={`cursor-pointer border-2 p-4 rounded-xl flex flex-col gap-2 transition-all ${settings.sp_accounting_method === 'GROSS' ? 'border-status-success bg-status-success-bg/50 shadow-md' : 'border-line hover:border-status-success/50 bg-surface-card'}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="text-status-success font-black uppercase text-sm">{t('finance_settings.method_gross')}</div>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${settings.sp_accounting_method === 'GROSS' ? 'border-status-success' : 'border-line'}`}>
                      {settings.sp_accounting_method === 'GROSS' && <div className="w-2 h-2 bg-status-success rounded-full"/>}
                    </div>
                  </div>
                  <p className="text-[10px] text-ink-secondary font-bold leading-relaxed">{t('finance_settings.method_gross_desc')}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-ink-secondary bg-surface-panel border border-line-subtle rounded-xl px-4 py-3">
                <ShieldCheck size={14} className="text-primary shrink-0" />
                {t('finance_settings.save_note')}
              </div>
            </div>
          )}
        </div>

        <div className={`p-6 bg-surface-panel flex justify-end gap-4 border-t border-line ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button onClick={onClose} className="flex items-center gap-2 px-6 py-2.5 text-xs font-black text-ink-secondary hover:text-ink uppercase italic transition-all">
            <RotateCcw size={14}/> {t('common.cancel')}
          </button>
          <button onClick={() => onSave(settings)} className="flex items-center gap-2 bg-primary-dark text-ink-inverse px-10 py-3 rounded-xl shadow-xl font-black text-xs uppercase hover:bg-primary hover:-translate-y-1 transition-all active:scale-95">
            <Save size={16}/> {t('common.save_settings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinanceSettingsModal;
